import React, { useState, useEffect, useRef } from 'react';
import Button from 'apollo-react/components/Button';
import TextField from 'apollo-react/components/TextField';
import Select from 'apollo-react/components/Select';
import MenuItem from 'apollo-react/components/MenuItem';
import Typography from 'apollo-react/components/Typography';
import Box from 'apollo-react/components/Box';
import Grid from 'apollo-react/components/Grid';
import Divider from 'apollo-react/components/Divider';
import ChevronLeft from 'apollo-react-icons/ChevronLeft';
import ChevronRight from 'apollo-react-icons/ChevronRight';
import CommentViewer from '../../../Guest/Requests/CommentViewer';
import ReviewItem from '../ReviewItem/ReviewItem';

const CommentSkeleton = () => (
  <Box sx={{ 
    marginBottom: 3, 
    padding: 2,
    borderRadius: 1, 
    backgroundColor: '#f8f9fa' 
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
      <Box sx={{ 
        width: 120, 
        height: 20, 
        backgroundColor: '#e0e0e0', 
        borderRadius: 0.5 
      }} />
      <Box sx={{ 
        width: 80, 
        height: 16, 
        backgroundColor: '#e0e0e0', 
        borderRadius: 0.5 
      }} />
    </Box>
    <Box sx={{ 
      width: '100%', 
      height: 20, 
      backgroundColor: '#e0e0e0', 
      borderRadius: 0.5, 
      marginBottom: 1 
    }} />
    <Box sx={{ 
      width: '70%', 
      height: 20, 
      backgroundColor: '#e0e0e0', 
      borderRadius: 0.5, 
      marginBottom: 2 
    }} />
    <Box sx={{ 
      width: 200, 
      height: 24, 
      backgroundColor: '#e0e0e0', 
      borderRadius: 12 
    }} />
  </Box>
);

const CommentsList = ({ 
  prevComments, 
  pagination, 
  goToPage, 
  nextPage, 
  prevPage,
  sortBy,
  sortOrder,
  updateSorting,
  filters,
  updateFilters,
  loadingComments,
  setPageLimit,
  reviews,
  persona = 'customer',
  showControls = true,
  showFilters = true,
  showSorting = true,
  showPageSize = true,
  showResultsInfo = true,
  showPagination = true,
  showCommentsTitle = true,
  onEdit,
  onDelete,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [animatedComments, setAnimatedComments] = useState([]);
  
  // Use ref to stabilize updateFilters function
  const updateFiltersRef = useRef(updateFilters);
  updateFiltersRef.current = updateFilters;

  // Simplified comment handling - REMOVED animation to stop infinite loops
  useEffect(() => {
    if (prevComments && prevComments.length > 0) {
      // Simply set comments without animation to prevent infinite loops
      setAnimatedComments(prevComments.map(c => ({ ...c, isNew: false })));
    } else {
      setAnimatedComments([]);
    }
  }, [prevComments]);

  // FIXED: Debounced search effect - stabilized dependency
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      updateFiltersRef.current({ search: searchTerm });
    }, 300); // 300ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]); // REMOVED unstable updateFilters dependency

  const handleSortChange = (field) => {
    updateSorting(field, sortOrder);
  };

  const handleOrderChange = (order) => {
    updateSorting(sortBy, order);
  };

  return (
    <Box>
     {/* Reviews Display */}
     {reviews && reviews.length > 0 && (
        <Box>
          {/* <Typography
              variant="title1"
              component="h2"
              sx={{
                fontWeight: 500,
                marginBottom: 3,
                color: "#333",
                paddingBottom: 1,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              Reviews
            </Typography> */}

          <Box>
            {reviews.map((review, index) => (
              <Box key={review.id || index} sx={{ marginBottom: 2 }}>
                <ReviewItem review={review} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      
      {/* Comments Section */}
      {prevComments && prevComments.length > 0 && (
        <Box sx={{ marginBottom: 4 }}>
            {showCommentsTitle && <Typography
              variant="title1"
              component="h2"
              sx={{
                fontWeight: 500,
                marginBottom: 3,
                color: "#333",
                paddingBottom: 1,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              Comments
            </Typography>}
            {/* Filters and Sorting Section */}
            {showControls && (showFilters || showSorting || showPageSize) && (
              <Box sx={{ marginBottom: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Search */}
                  {showFilters && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        placeholder="Search comments..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 300 }}
                      />
                    </Grid>
                  )}
                  
                  {/* Sort and Page Size Controls */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {/* Sort Controls */}
                      {showSorting && (
                        <>
                          <Typography variant="bodySmall" sx={{ color: '#666', minWidth: 'fit-content' }}>
                            Sort by:
                          </Typography>
                          <Select
                            value={sortBy}
                            onChange={(e) => handleSortChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 100 }}
                          >
                            <MenuItem value="createdAt">Date</MenuItem>
                            <MenuItem value="comment">Comment</MenuItem>
                          </Select>
                          <Select
                            value={sortOrder}
                            onChange={(e) => handleOrderChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="DESC">Descending</MenuItem>
                            <MenuItem value="ASC">Ascending</MenuItem>
                          </Select>
                        </>
                      )}

                      {/* Page Size */}
                      {showPageSize && (
                        <>
                          <Typography variant="bodySmall" sx={{ color: '#666', minWidth: 'fit-content' }}>
                            Show:
                          </Typography>
                          <Select
                            value={pagination.limit}
                            onChange={(e) => setPageLimit(e.target.value)}
                            size="small"
                            sx={{ minWidth: 80 }}
                          >
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={200}>200</MenuItem>
                          </Select>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Results Info */}
            {showResultsInfo && showControls && (
              <Box sx={{ marginBottom: 2 }}>
                <Typography variant="bodySmall" sx={{ color: '#666', fontWeight: 500 }}>
                  Showing {Math.min(((pagination.currentPage - 1) * pagination.limit) + 1, pagination.totalCount)} - {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount} comments
                </Typography>
              </Box>
            )}

            {/* Comments Display */}
            <Box>
              {loadingComments && animatedComments.length === 0 ? (
                <Box>
                  <CommentSkeleton />
                  <CommentSkeleton />
                  <CommentSkeleton />
                </Box>
              ) : !loadingComments && animatedComments.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: 100
                }}>
                  <Typography variant="bodyDefault" color="textSecondary">
                    No comments added yet
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {animatedComments.map((comment, index) => (
                    <React.Fragment key={comment.id}>
                      <Box 
                        sx={{
                          marginBottom: 2,
                          opacity: comment.isNew ? 0 : 1,
                          transform: comment.isNew ? 'translateY(-10px)' : 'translateY(0)',
                          transition: 'all 0.3s ease-in-out'
                        }}
                      >
                        <CommentViewer 
                          comments={[comment]} 
                          persona={persona} 
                          commentIndex={index}
                          onEdit={onEdit}
                          onDelete={onDelete}
                        />
                      </Box>
                      {/* divider between comments, but not after the last one */}
                      {index < animatedComments.length - 1 && (
                        <Divider sx={{ marginY: 2, borderColor: "#E1E1E1" }} />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>

            {/* Pagination Controls */}
            {showPagination && showControls && pagination.totalPages > 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 1, 
                marginTop: 3
              }}>
                <Button
                  onClick={prevPage}
                  disabled={!pagination.hasPrevPage || loadingComments}
                  variant="outlined"
                  size="small"
                  startIcon={<ChevronLeft />}
                  sx={{ minWidth: 100 }}
                >
                  Previous
                </Button>

                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        disabled={loadingComments}
                        variant={pageNum === pagination.currentPage ? "contained" : "outlined"}
                        size="small"
                        sx={{ 
                          minWidth: 40, 
                          height: 32,
                          backgroundColor: pageNum === pagination.currentPage ? '#0557D5' : 'transparent',
                          '&:hover': {
                            backgroundColor: pageNum === pagination.currentPage ? '#0557D5' : '#f0f0f0'
                          }
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </Box>

                <Button
                  onClick={nextPage}
                  disabled={!pagination.hasNextPage || loadingComments}
                  variant="outlined"
                  size="small"
                  endIcon={<ChevronRight />}
                  sx={{ minWidth: 100 }}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
      )}
    </Box>
  );
};

export default CommentsList; 